
import { Options, Vue } from 'vue-class-component';
import api from '@/shared/api';

type CreditBundleSummary = {
    sku: string;
    label: string;
    description: string;
    credits: number;
    creditsLabel: string;
    validityDays: number;
    validityLabel: string;
    priceEur: number;
    priceLabel: string;
    currency: string;
    purchasable: boolean;
    contactHref: string | null;
};

type CreditLotRow = {
    id: string;
    source: string;
    sourceRefId: string;
    status: string;
    grantedAmount: number;
    remainingAmount: number;
    grantedAt: string;
    expiresAt: string;
    depletedAt: string | null;
    spendable: boolean;
};

type WorkspaceSubscriptionLastBilling = {
    orderId: string;
    orderStatus: string;
    failed: boolean;
    createdAt: string | null;
};

type WorkspaceSubscriptionSummary = {
    type: 'trial' | 'paid';
    planLabel: string;
    monthlyCredits: number;
    renewalInterval: string;
    renewalIntervalLabel: string;
    periodEndAt: string | null;
    automaticRenewal: boolean;
    periodEndLabel: 'renews' | 'expires';
    lastBilling: WorkspaceSubscriptionLastBilling | null;
};

type CreditsOverviewData = {
    businessUnitId: string;
    available: number;
    reserved: number;
    nextExpiryAt: string | null;
    lots: CreditLotRow[];
    subscription: WorkspaceSubscriptionSummary | null;
};

@Options({})
export default class CreditsOverview extends Vue {
    loading = true;
    error = '';
    overview: CreditsOverviewData | null = null;
    bundlesLoading = true;
    bundlesError = '';
    creditBundles: CreditBundleSummary[] = [];
    buyingBundleSku: string | null = null;
    bundlePurchaseError = '';

    get subscriptionManageRoute(): { name: string } {
        const type = this.overview?.subscription?.type;
        return {
            name: type === 'trial' ? 'subscription-upgrade' : 'subscription-extend',
        };
    }

    get periodEndHeading(): string {
        const sub = this.overview?.subscription;
        if (!sub) {
            return 'Period end';
        }
        return sub.periodEndLabel === 'renews' ? 'Renews on' : 'Expires on';
    }

    async created() {
        await Promise.all([this.loadOverview(), this.loadBundles()]);
    }

    async loadOverview() {
        this.loading = true;
        this.error = '';
        try {
            this.overview = await api.getCreditsOverview();
        } catch (e) {
            this.overview = null;
            this.error = 'Could not load credit balance.';
            console.error(e);
        } finally {
            this.loading = false;
        }
    }

    async loadBundles() {
        this.bundlesLoading = true;
        this.bundlesError = '';
        try {
            const response = await api.getCreditBundles();
            this.creditBundles = response.bundles ?? [];
        } catch (e) {
            this.creditBundles = [];
            this.bundlesError = 'Could not load credit bundles.';
            console.error(e);
        } finally {
            this.bundlesLoading = false;
        }
    }

    async onBuyBundle(bundle: CreditBundleSummary) {
        if (!bundle.purchasable) {
            return;
        }
        if (this.buyingBundleSku) {
            return;
        }

        this.bundlePurchaseError = '';
        this.buyingBundleSku = bundle.sku;

        try {
            const { orderId, paymentRequired } = await api.createCreditBundleOrder(bundle.sku);

            if (paymentRequired) {
                const payResponse = await api.molliePay({ orderId });
                if (payResponse?.url) {
                    window.location.href = payResponse.url;
                    return;
                }
                throw new Error('Payment could not be started.');
            }

            await api.completeCreditBundlePayment(orderId);
            await this.loadOverview();
        } catch (e: unknown) {
            this.bundlePurchaseError = e instanceof Error ? e.message : 'Could not start bundle purchase.';
        } finally {
            this.buyingBundleSku = null;
        }
    }

    formatAmount(value: number): string {
        return new Intl.NumberFormat().format(value);
    }

    formatTableDate(iso: string): string {
        const date = new Date(iso);
        if (isNaN(date.getTime())) {
            return '—';
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    formatFullDate(iso: string): string {
        const date = new Date(iso);
        if (isNaN(date.getTime())) {
            return '—';
        }
        return date.toLocaleDateString(undefined, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }

    remainingDaysUntil(iso: string): number | null {
        const target = new Date(iso);
        if (isNaN(target.getTime())) {
            return null;
        }
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfTarget = new Date(target.getFullYear(), target.getMonth(), target.getDate());
        const diffMs = startOfTarget.getTime() - startOfToday.getTime();
        return Math.round(diffMs / (1000 * 60 * 60 * 24));
    }

    formatRemainingDays(iso: string): string {
        const days = this.remainingDaysUntil(iso);
        if (days === null) {
            return '—';
        }
        if (days < 0) {
            return 'Expired';
        }
        if (days === 0) {
            return 'Today';
        }
        if (days === 1) {
            return '1 day';
        }
        return `${days} days`;
    }

    statusBadgeClass(status: string): string {
        switch (status) {
            case 'active':
                return 'text-bg-success';
            case 'depleted':
                return 'text-bg-secondary';
            case 'expired':
                return 'text-bg-warning';
            case 'revoked':
                return 'text-bg-danger';
            default:
                return 'text-bg-light';
        }
    }

    isLastBillingSuccessful(lastBilling: WorkspaceSubscriptionLastBilling): boolean {
        return lastBilling.orderStatus === 'fulfilled';
    }

    lastBillingBadgeClass(successful: boolean): string {
        return successful ? 'text-bg-success' : 'text-bg-danger';
    }
}
